(() => {
if (customElements.get('product-modal')) return;
class RnfProductModal extends ModalDialog {
  constructor() {
    super();
    this.rnfEnabled = this.hasAttribute('data-rnf-lightbox');
    if (!this.rnfEnabled) return;
    this.items = JSON.parse(this.querySelector('[data-rnf-lightbox-items]').textContent);
    this.photo = this.querySelector('[data-rnf-lightbox-photo]');
    this.frame = this.querySelector('[data-rnf-lightbox-frame]');
    this.stage = this.querySelector('[data-rnf-lightbox-stage]');
    this.counter = this.querySelector('[data-rnf-lightbox-counter]');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.revision = 0;
    this.index = 0;
    this.zoom = 1;
    this.pan = {x:0,y:0};
    this.pointers = new Set();
    this.querySelectorAll('[data-rnf-lightbox-step]').forEach(button => {
      button.hidden = this.items.length < 2;
      button.addEventListener('click', () => this.select(this.index + Number(button.dataset.rnfLightboxStep), Number(button.dataset.rnfLightboxStep)));
    });
    this.addEventListener('keydown', event => this.onKey(event));
    this.stage.addEventListener('click', event => {
      if (Date.now() < (this.ignoreClickUntil || 0)) return;
      if (!this.insideImage(event)) this.hide();
      else this.toggleZoom();
    });
    this.stage.addEventListener('pointerdown', event => this.startPointer(event));
    this.stage.addEventListener('pointermove', event => this.movePointer(event));
    this.stage.addEventListener('pointerup', event => this.endPointer(event));
    this.stage.addEventListener('pointercancel', event => this.endPointer(event,true));
    this.onResize = () => { if (this.hasAttribute('open')) this.resetZoom(); };
  }

  show(opener) {
    if (!this.rnfEnabled) return super.show(opener);
    const reopening = this.hasAttribute('open');
    clearTimeout(this.closeTimer);
    this.classList.remove('is-closing');
    this.openedBy = opener;
    const original = opener.closest('modal-opener')?.querySelector('.product__media img');
    const source = original?.dataset.rnfWheelSource || original?.currentSrc || original?.src;
    const key = value => { try { return new URL(value,location.href).pathname; } catch { return ''; } };
    let index = source ? this.items.findIndex(item => key(item.src) === key(source)) : -1;
    if (index < 0) index = this.items.findIndex(item => item.id === opener.dataset.mediaId);
    if (!reopening) {
      this.playingVideos = [...document.querySelectorAll('[data-rnf-autoplay-video] video, [data-rnf-story-video] video')].filter(video => !video.paused);
      this.oldPadding = document.body.style.paddingRight;
      const gutter = Math.max(0, innerWidth - document.documentElement.clientWidth);
      if (gutter) document.body.style.paddingRight = `${parseFloat(getComputedStyle(document.body).paddingRight) + gutter}px`;
    }
    this.select(Math.max(0,index),0,original?.currentSrc || original?.src);
    document.body.classList.add('rnf-lightbox-open');
    super.show(opener);
    window.addEventListener('resize',this.onResize,{passive:true});
  }

  hide(immediate = false) {
    if (!this.rnfEnabled) return super.hide();
    if (!this.hasAttribute('open') || this.classList.contains('is-closing')) return;
    this.revision++;
    this.classList.add('is-closing');
    this.clearRequest();
    this.closeTimer = setTimeout(() => this.finishClose(), immediate || this.reduced.matches ? 0 : 220);
  }

  finishClose() {
    window.__rnfCarouselMedia?.unmountWheels(this.photo);
    this.photo.querySelector('img')?.remove();
    if (this.hasAttribute('open')) super.hide();
    this.classList.remove('is-closing');
    document.body.classList.remove('rnf-lightbox-open');
    document.body.style.paddingRight = this.oldPadding || '';
    window.removeEventListener('resize',this.onResize);
    this.playingVideos?.forEach(video => {
      const rect = video.getBoundingClientRect();
      if (video.isConnected && video.muted && rect.bottom > 0 && rect.top < innerHeight) video.play().catch(() => {});
    });
    this.pointers.clear();
    this.gesture = null;
  }

  disconnectedCallback() {
    if (!this.rnfEnabled || this.isConnected) return;
    clearTimeout(this.closeTimer);
    this.revision++;
    this.clearRequest();
    if (this.hasAttribute('open')) this.finishClose();
    else window.__rnfCarouselMedia?.unmountWheels(this.photo);
  }

  clearRequest() {
    if (!this.request) return;
    this.request.onload = this.request.onerror = null;
    this.request = null;
  }

  select(index,direction = 0,preview) {
    if (!this.items.length) return;
    this.index = (index + this.items.length) % this.items.length;
    this.resetZoom();
    const item = this.items[this.index];
    const revision = ++this.revision;
    this.clearRequest();
    const numbers = new Intl.NumberFormat(document.documentElement.lang || undefined);
    this.counter.textContent = `${numbers.format(this.index + 1)} / ${numbers.format(this.items.length)}`;
    this.stage.setAttribute('aria-busy','true');
    if (preview) this.render(item,preview,0);
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    this.request = image;
    const source = item.still || item.src;
    const finish = async () => {
      try { await image.decode(); } catch {}
      if (revision !== this.revision || !this.hasAttribute('open') || !image.naturalWidth) return;
      this.render(item,image.currentSrc || image.src,direction);
      this.stage.setAttribute('aria-busy','false');
      this.request = null;
    };
    image.onload = finish;
    image.onerror = () => {
      if (revision !== this.revision || !this.hasAttribute('open')) return;
      const native = new URL(item.still || item.src,location.href);
      native.searchParams.delete('width');
      const fallback = native.href;
      if (image.src !== fallback) image.src = fallback;
      else { this.stage.setAttribute('aria-busy','false'); this.request = null; }
    };
    image.src = source;
  }

  render(item,source,direction) {
    const focused = document.activeElement === this.photo.querySelector('img');
    window.__rnfCarouselMedia?.unmountWheels(this.photo);
    this.photo.querySelector('img')?.remove();
    const image = new Image(item.width,item.height);
    image.alt = item.alt;
    image.decoding = 'async';
    image.tabIndex = 0;
    image.setAttribute('role','button');
    image.setAttribute('aria-pressed',String(this.zoom > 1));
    if (item.mask) image.setAttribute('data-rnf-badge-mask','');
    image.src = source;
    this.photo.prepend(image);
    if (focused) image.focus({preventScroll:true});
    const primarySource = new URL(item.still || item.src,location.href).href;
    if (!this.photo.hasAttribute('data-rnf-automatic-media') && new URL(source,location.href).href === primarySource) window.__rnfCarouselMedia?.updateWheel(image,item.src);
    this.updateZoomAvailability();
    if (!this.reduced.matches) this.frame.animate([
      {opacity:.25,transform:`translate3d(${direction * 18}px,0,0) scale(.985)`},
      {opacity:1,transform:'translate3d(0,0,0) scale(1)'}
    ],{duration:320,easing:'cubic-bezier(.22,.72,.18,1)'});
  }

  imageMetrics() {
    const item = this.items[this.index];
    const width = this.stage.clientWidth;
    const height = this.stage.clientHeight;
    const scale = Math.min(width / item.width,height / item.height);
    return {width,height,imageWidth:item.width * scale,imageHeight:item.height * scale,scale};
  }

  insideImage(event) {
    const metrics = this.imageMetrics();
    const rect = this.stage.getBoundingClientRect();
    return Math.abs(event.clientX - rect.left - metrics.width / 2) <= metrics.imageWidth * this.zoom / 2 &&
      Math.abs(event.clientY - rect.top - metrics.height / 2) <= metrics.imageHeight * this.zoom / 2;
  }

  updateZoomAvailability() {
    this.classList.toggle('can-zoom',1 / this.imageMetrics().scale > 1.05);
  }

  resetZoom() {
    this.zoom = 1;
    this.pan = {x:0,y:0};
    this.applyPan();
    this.classList.remove('is-zoomed','is-dragging');
    this.photo.querySelector('img')?.setAttribute('aria-pressed','false');
    if (this.items?.length) this.updateZoomAvailability();
  }

  toggleZoom() {
    if (this.zoom > 1) return this.resetZoom();
    this.zoom = Math.min(3,Math.max(1,1 / this.imageMetrics().scale));
    this.classList.toggle('is-zoomed',this.zoom > 1);
    this.photo.querySelector('img')?.setAttribute('aria-pressed',String(this.zoom > 1));
    this.applyPan();
  }

  applyPan() {
    this.photo.style.setProperty('--rnf-lightbox-zoom',String(this.zoom));
    this.photo.style.setProperty('--rnf-lightbox-x',`${this.pan.x}px`);
    this.photo.style.setProperty('--rnf-lightbox-y',`${this.pan.y}px`);
  }

  onKey(event) {
    if (!this.rnfEnabled || !this.hasAttribute('open')) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (['ArrowLeft','ArrowRight'].includes(event.key)) {
      event.preventDefault();
      this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1), event.key === 'ArrowRight' ? 1 : -1);
    } else if (['+','=','Enter',' '].includes(event.key) && (event.target.tagName === 'IMG' || ['+','='].includes(event.key))) {
      event.preventDefault(); this.toggleZoom();
    } else if (['-','0'].includes(event.key)) { event.preventDefault(); this.resetZoom(); }
  }

  startPointer(event) {
    this.pointers.add(event.pointerId);
    if (this.pointers.size > 1) { this.gesture = null; return; }
    this.gesture = {id:event.pointerId,x:event.clientX,y:event.clientY,pan:{...this.pan},moved:false};
    this.stage.setPointerCapture(event.pointerId);
  }

  movePointer(event) {
    const gesture = this.gesture;
    if (!gesture || gesture.id !== event.pointerId || this.pointers.size !== 1) return;
    const x = event.clientX - gesture.x;
    const y = event.clientY - gesture.y;
    if (Math.abs(x) + Math.abs(y) > 8) gesture.moved = true;
    if (this.zoom <= 1 || !gesture.moved) return;
    const metrics = this.imageMetrics();
    const limitX = Math.max(0,(metrics.imageWidth * this.zoom - metrics.width) / 2);
    const limitY = Math.max(0,(metrics.imageHeight * this.zoom - metrics.height) / 2);
    this.pan = {x:Math.max(-limitX,Math.min(limitX,gesture.pan.x + x)),y:Math.max(-limitY,Math.min(limitY,gesture.pan.y + y))};
    this.classList.add('is-dragging');
    this.applyPan();
  }

  endPointer(event,cancelled = false) {
    this.pointers.delete(event.pointerId);
    const gesture = this.gesture;
    if (!gesture || gesture.id !== event.pointerId) return;
    this.gesture = null;
    this.classList.remove('is-dragging');
    if (cancelled || !gesture.moved) return;
    this.ignoreClickUntil = Date.now() + 350;
    if (this.zoom > 1) return;
    const x = event.clientX - gesture.x;
    const y = event.clientY - gesture.y;
    if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.2) this.select(this.index + (x < 0 ? 1 : -1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1), x < 0 ? 1 : -1);
    else if (y > 100 && Math.abs(y) > Math.abs(x) * 1.2) this.hide();
  }
}


if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends RnfProductModal {
      constructor() {
        super();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        if (!this.rnfEnabled) this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}

})();
